<script setup>
import { computed, getCurrentInstance, onMounted } from 'vue'
import { useForm } from '@inertiajs/vue3';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import MainLayout from '@/layout/main/MainLayout.vue';
import { useLoading } from '@/composables/useLoading';

// Determine layout based on URL path
const isAdminContext = computed(() => {
    return window.location.pathname.startsWith('/admin/');
});

defineOptions({
    layout: (h, page) => {
        const isAdminContext = window.location.pathname.startsWith('/admin/');
        return isAdminContext
            ? h(AdminMainLayout, () => page)
            : h(MainLayout, () => page);
    },
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const { showLoading, hideLoading } = useLoading();

const props = defineProps({
    coins: Object,
    categories: Array,
    languages: Array,
    countries: Array,
    sellers: Array,
    teams: Array,
    types: Array,
});

const form = useForm({
    url: '',
    category_id: null,
    da: '',
    dr: '',
    traffic: '',
    language_id: null,
    country_id: null,
    link_type: null,

    gambling: false,
    cdb: false,
    cripto: false,
    sponsor: false,
    menu: false,
    banner: false,

    cost: '',
    cost_coin: 'BRL',

    sale: '',
    sale_coin: 'BRL',

    last_posted: '',
    seller_id: null,
    team_id: null,

    obs: '',

    owner_name: '',
    owner_email: '',
    owner_phone: '',
    bank: '',
    pix: '',
    phone: '',
    paypal: '',
    instagram: '',
    facebook: '',

    owner_role: '',
    promo: false,
    restrict: false,
    restrict_detail: '',
    archive_article: false,
    archive_due: '',
    links: null,
    embed: false,
    images: null,
    example_article: '',
    rules: '',
    google_news: false,
    global_account: '',

    types: props.types,
});

const submit = () => {
    showLoading($t('Creating site...'));
    form.post(proxy.route ? proxy.route('sites.store') : '/admin/sites', {
        onFinish: () => hideLoading(),
    });
}

const linkOptions = [
    { label: 'Selecione', value: null },
    { label: 'DOFOLLOW', value: 'DOFOLLOW' },
    { label: 'NOFOLLOW', value: 'NOFOLLOW' }
];

const linkLimitOptions = [
    { label: '3', value: 3 },
    { label: '5', value: 5 },
    { label: $t('No limit'), value: null }
];

const imageLimitOptions = [
    { label: '1', value: 1 },
    { label: '3', value: 3 },
    { label: '5', value: 5 }
];

const currencyOptions = [
    { label: 'BRL', value: 'BRL' },
    { label: 'EUR', value: 'EUR' },
    { label: 'USD', value: 'USD' },
    { label: 'GBP', value: 'GBP' }
];

// Format currency inputs
const formatCurrency = (value, currency) => {
    if (!value) return '';
    const config = props.coins[currency];
    if (!config) return value;

    // Simple formatting - in a real app you'd use a proper money formatting library
    return new Intl.NumberFormat(config.locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: config.precision
    }).format(parseFloat(value) || 0);
};

const formatDate = (date) => {
    if(!date) return ''
    return date.toISOString().split('T')[0]
}

onMounted(() => {
    // Initialize form with proper structure
    form.types = form.types.map(type => ({
        ...type,
        available: false,
        cost: '',
        cost_coin: 'BRL',
        sale: '',
        sale_coin: 'BRL'
    }));
});
</script>

<template>
    <div class="grid">
        <Card class="mb-6">
            <template #header>
                <div class="flex items-center gap-3 p-6 pb-0">
                    <i class="pi pi-plus text-2xl text-primary-500" />
                    <div>
                        <h2 class="text-xl font-bold">{{ $t('Add site') }}</h2>
                        <span class="text-sm text-surface-500">{{ $t('Create a new site entry') }}</span>
                    </div>
                </div>
            </template>

            <template #content>
                <form @submit.prevent="submit" class="grid grid-cols-12 gap-4">

                    <!-- URL -->
                    <div class="col-span-12">
                        <label class="block text-sm font-medium text-surface-700 mb-2">
                            {{ $t('URL') }} *
                        </label>
                        <InputText
                            v-model="form.url"
                            class="w-full"
                            required
                            autofocus
                            placeholder="https://exemplo.com"
                        />
                        <small v-if="form.errors.url" class="text-red-600">{{ form.errors.url }}</small>
                    </div>

                    <!-- Category -->
                    <div class="col-span-12">
                        <label class="block text-sm font-medium text-surface-700 mb-2">
                            {{ $t('Category') }}
                        </label>
                        <Select
                            v-model="form.category_id"
                            :options="categories"
                            optionLabel="name"
                            optionValue="id"
                            :placeholder="$t('Select...')"
                            class="w-full"
                        />
                        <small v-if="form.errors.category_id" class="text-red-600">{{ form.errors.category_id }}</small>
                    </div>

                    <!-- Metrics -->
                    <div class="col-span-4">
                        <label class="block text-sm font-medium text-surface-700 mb-2">
                            {{ $t('DA') }}
                        </label>
                        <InputNumber
                            v-model="form.da"
                            class="w-full"
                            :min="0"
                            :max="100"
                            :minFractionDigits="0"
                            :maxFractionDigits="2"
                        />
                        <small v-if="form.errors.da" class="text-red-600">{{ form.errors.da }}</small>
                    </div>

                    <div class="col-span-4">
                        <label class="block text-sm font-medium text-surface-700 mb-2">
                            {{ $t('DR') }}
                        </label>
                        <InputNumber
                            v-model="form.dr"
                            class="w-full"
                            :min="0"
                            :max="100"
                            :minFractionDigits="0"
                            :maxFractionDigits="2"
                        />
                        <small v-if="form.errors.dr" class="text-red-600">{{ form.errors.dr }}</small>
                    </div>

                    <div class="col-span-4">
                        <label class="block text-sm font-medium text-surface-700 mb-2">
                            {{ $t('Traffic') }}
                        </label>
                        <InputNumber
                            v-model="form.traffic"
                            class="w-full"
                            :min="0"
                            :minFractionDigits="0"
                            :maxFractionDigits="2"
                        />
                        <small v-if="form.errors.traffic" class="text-red-600">{{ form.errors.traffic }}</small>
                    </div>

                    <!-- Language and Country -->
                    <div class="col-span-6">
                        <label class="block text-sm font-medium text-surface-700 mb-2">
                            {{ $t('Language') }}
                        </label>
                        <Select
                            v-model="form.language_id"
                            :options="languages"
                            optionLabel="name"
                            optionValue="id"
                            :placeholder="$t('Select...')"
                            class="w-full"
                        />
                        <small v-if="form.errors.language_id" class="text-red-600">{{ form.errors.language_id }}</small>
                    </div>

                    <div class="col-span-6">
                        <label class="block text-sm font-medium text-surface-700 mb-2">
                            {{ $t('Country') }}
                        </label>
                        <Select
                            v-model="form.country_id"
                            :options="countries"
                            optionLabel="name"
                            optionValue="id"
                            :placeholder="$t('Select...')"
                            class="w-full"
                        />
                        <small v-if="form.errors.country_id" class="text-red-600">{{ form.errors.country_id }}</small>
                    </div>

                    <!-- Link Type -->
                    <div class="col-span-12">
                        <label class="block text-sm font-medium text-surface-700 mb-2">
                            {{ $t('Link type') }}
                        </label>
                        <Select
                            v-model="form.link_type"
                            :options="linkOptions"
                            optionLabel="label"
                            optionValue="value"
                            :placeholder="$t('Select...')"
                            class="w-full"
                        />
                        <small v-if="form.errors.link_type" class="text-red-600">{{ form.errors.link_type }}</small>
                    </div>

                    <Divider />

                    <!-- Checkboxes -->
                    <div class="col-span-12">
                        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div class="flex items-center">
                                <Checkbox v-model="form.sponsor" inputId="sponsor" :binary="true" />
                                <label for="sponsor" class="ml-2">{{ $t('Sponsor') }}</label>
                            </div>
                            <div class="flex items-center">
                                <Checkbox v-model="form.menu" inputId="menu" :binary="true" />
                                <label for="menu" class="ml-2">{{ $t('Menu') }}</label>
                            </div>
                            <div class="flex items-center">
                                <Checkbox v-model="form.banner" inputId="banner" :binary="true" />
                                <label for="banner" class="ml-2">{{ $t('Banner') }}</label>
                            </div>
                            <div class="flex items-center">
                                <Checkbox v-model="form.promo" inputId="promo" :binary="true" />
                                <label for="promo" class="ml-2">{{ $t('Promo') }}</label>
                            </div>
                            <div class="flex items-center">
                                <Checkbox v-model="form.google_news" inputId="google_news" :binary="true" />
                                <label for="google_news" class="ml-2">{{ $t('Google News') }}</label>
                            </div>
                            <div class="flex items-center">
                                <Checkbox v-model="form.embed" inputId="embed" :binary="true" />
                                <label for="embed" class="ml-2">{{ $t('Embed') }}</label>
                            </div>
                        </div>

                        <!-- Conditional fields -->
                        <div class="mt-4 space-y-4">
                            <div>
                                <div class="flex items-center mb-2">
                                    <Checkbox v-model="form.restrict" inputId="restrict" :binary="true" />
                                    <label for="restrict" class="ml-2">{{ $t('Restriction') }}</label>
                                </div>
                                <InputText
                                    v-if="form.restrict"
                                    v-model="form.restrict_detail"
                                    class="w-full"
                                    :placeholder="$t('Inform about the restriction')"
                                />
                            </div>

                            <div>
                                <div class="flex items-center mb-2">
                                    <Checkbox v-model="form.archive_article" inputId="archive_article" :binary="true" />
                                    <label for="archive_article" class="ml-2">{{ $t('Archive article') }}</label>
                                </div>
                                <InputText
                                    v-if="form.archive_article"
                                    v-model="form.archive_due"
                                    class="w-full"
                                    :placeholder="$t('Inform how long')"
                                />
                            </div>
                        </div>
                    </div>

                    <Divider />

                    <!-- Number of links -->
                    <div class="col-span-12">
                        <label class="block text-sm font-medium text-surface-700 mb-3">
                            {{ $t('Number of links in the article') }}
                        </label>
                        <div class="flex gap-4">
                            <div
                                v-for="option in linkLimitOptions"
                                :key="option.value"
                                class="flex items-center"
                            >
                                <RadioButton
                                    v-model="form.links"
                                    :inputId="`links-${option.value}`"
                                    :value="option.value"
                                />
                                <label :for="`links-${option.value}`" class="ml-2">{{ option.label }}</label>
                            </div>
                        </div>
                    </div>

                    <!-- Maximum images -->
                    <div class="col-span-12">
                        <label class="block text-sm font-medium text-surface-700 mb-3">
                            {{ $t('Maximum number of images in content') }}
                        </label>
                        <div class="flex gap-4">
                            <div
                                v-for="option in imageLimitOptions"
                                :key="option.value"
                                class="flex items-center"
                            >
                                <RadioButton
                                    v-model="form.images"
                                    :inputId="`images-${option.value}`"
                                    :value="option.value"
                                />
                                <label :for="`images-${option.value}`" class="ml-2">{{ option.label }}</label>
                            </div>
                        </div>
                    </div>

                    <Divider />

                    <!-- Pricing Table -->
                    <div class="col-span-12">
                        <h3 class="text-lg font-semibold mb-4">{{ $t('Pricing table') }}</h3>

                        <DataTable :value="[{ name: $t('Default'), isDefault: true }, ...form.types]" class="mb-4">
                            <Column field="name" :header="$t('Type')" class="font-medium" />

                            <Column :header="$t('Available')" headerClass="text-center" bodyClass="text-center">
                                <template #body="slotProps">
                                    <Checkbox
                                        v-if="!slotProps.data.isDefault"
                                        v-model="slotProps.data.available"
                                        :binary="true"
                                    />
                                </template>
                            </Column>

                            <Column :header="$t('Cost')">
                                <template #body="slotProps">
                                    <div v-if="slotProps.data.isDefault" class="flex gap-2">
                                        <InputText
                                            v-model="form.cost"
                                            class="flex-1"
                                            placeholder="0,00"
                                        />
                                        <Select
                                            v-model="form.cost_coin"
                                            :options="currencyOptions"
                                            optionLabel="label"
                                            optionValue="value"
                                            class="w-20"
                                        />
                                    </div>
                                    <div v-else class="flex gap-2" :class="{ 'opacity-50': !slotProps.data.available }">
                                        <InputText
                                            v-model="slotProps.data.cost"
                                            :disabled="!slotProps.data.available"
                                            class="flex-1"
                                            placeholder="0,00"
                                        />
                                        <Select
                                            v-model="slotProps.data.cost_coin"
                                            :options="currencyOptions"
                                            optionLabel="label"
                                            optionValue="value"
                                            :disabled="!slotProps.data.available"
                                            class="w-20"
                                        />
                                    </div>
                                </template>
                            </Column>

                            <Column :header="$t('Sale')">
                                <template #body="slotProps">
                                    <div v-if="slotProps.data.isDefault" class="flex gap-2">
                                        <InputText
                                            v-model="form.sale"
                                            class="flex-1"
                                            placeholder="0,00"
                                        />
                                        <Select
                                            v-model="form.sale_coin"
                                            :options="currencyOptions"
                                            optionLabel="label"
                                            optionValue="value"
                                            class="w-20"
                                        />
                                    </div>
                                    <div v-else class="flex gap-2" :class="{ 'opacity-50': !slotProps.data.available }">
                                        <InputText
                                            v-model="slotProps.data.sale"
                                            :disabled="!slotProps.data.available"
                                            class="flex-1"
                                            placeholder="0,00"
                                        />
                                        <Select
                                            v-model="slotProps.data.sale_coin"
                                            :options="currencyOptions"
                                            optionLabel="label"
                                            optionValue="value"
                                            :disabled="!slotProps.data.available"
                                            class="w-20"
                                        />
                                    </div>
                                </template>
                            </Column>
                        </DataTable>
                    </div>

                    <Divider />

                    <!-- Additional Fields -->
                    <div class="col-span-12">
                        <label class="block text-sm font-medium text-surface-700 mb-2">
                            {{ $t('Last posted') }}
                        </label>
                        <Calendar
                            v-model="form.last_posted"
                            showIcon
                            class="w-full"
                            dateFormat="dd/mm/yy"
                            @update:modelValue="(value) => form.last_posted = formatDate(value)"
                        />
                        <small v-if="form.errors.last_posted" class="text-red-600">{{ form.errors.last_posted }}</small>
                    </div>

                    <div class="col-span-12">
                        <label class="block text-sm font-medium text-surface-700 mb-2">
                            {{ $t('Seller') }}
                        </label>
                        <Select
                            v-model="form.seller_id"
                            :options="sellers"
                            optionLabel="name"
                            optionValue="id"
                            :placeholder="$t('Select...')"
                            class="w-full"
                        />
                        <small v-if="form.errors.seller_id" class="text-red-600">{{ form.errors.seller_id }}</small>
                    </div>

                    <div class="col-span-12">
                        <label class="block text-sm font-medium text-surface-700 mb-2">
                            {{ $t('Obs') }}
                        </label>
                        <Textarea
                            v-model="form.obs"
                            rows="3"
                            class="w-full"
                        />
                        <small v-if="form.errors.obs" class="text-red-600">{{ form.errors.obs }}</small>
                    </div>

                    <div class="col-span-12">
                        <label class="block text-sm font-medium text-surface-700 mb-2">
                            {{ $t('Rules for content') }}
                        </label>
                        <Textarea
                            v-model="form.rules"
                            rows="3"
                            class="w-full"
                        />
                        <small v-if="form.errors.rules" class="text-red-600">{{ form.errors.rules }}</small>
                    </div>

                    <div class="col-span-12">
                        <label class="block text-sm font-medium text-surface-700 mb-2">
                            {{ $t('Example article') }}
                        </label>
                        <InputText
                            v-model="form.example_article"
                            class="w-full"
                            placeholder="https://..."
                        />
                        <small v-if="form.errors.example_article" class="text-red-600">{{ form.errors.example_article }}</small>
                    </div>

                    <Divider />

                    <!-- Owner Information -->
                    <div class="col-span-12">
                        <h3 class="text-lg font-semibold mb-4">{{ $t('Owner Information') }}</h3>
                    </div>

                    <div class="col-span-8">
                        <label class="block text-sm font-medium text-surface-700 mb-2">
                            {{ $t('Owner name') }}
                        </label>
                        <InputText
                            v-model="form.owner_name"
                            class="w-full"
                        />
                        <small v-if="form.errors.owner_name" class="text-red-600">{{ form.errors.owner_name }}</small>
                    </div>

                    <div class="col-span-4">
                        <label class="block text-sm font-medium text-surface-700 mb-2">
                            {{ $t('Owner role') }}
                        </label>
                        <InputText
                            v-model="form.owner_role"
                            class="w-full"
                        />
                        <small v-if="form.errors.owner_role" class="text-red-600">{{ form.errors.owner_role }}</small>
                    </div>

                    <div class="col-span-6">
                        <label class="block text-sm font-medium text-surface-700 mb-2">
                            {{ $t('Owner email') }}
                        </label>
                        <InputText
                            v-model="form.owner_email"
                            type="email"
                            class="w-full"
                        />
                        <small v-if="form.errors.owner_email" class="text-red-600">{{ form.errors.owner_email }}</small>
                    </div>

                    <div class="col-span-6">
                        <label class="block text-sm font-medium text-surface-700 mb-2">
                            {{ $t('Owner phone') }}
                        </label>
                        <InputText
                            v-model="form.owner_phone"
                            class="w-full"
                        />
                        <small v-if="form.errors.owner_phone" class="text-red-600">{{ form.errors.owner_phone }}</small>
                    </div>

                    <div class="col-span-12">
                        <label class="block text-sm font-medium text-surface-700 mb-2">
                            {{ $t('Site bank') }}
                        </label>
                        <Textarea
                            v-model="form.bank"
                            rows="3"
                            class="w-full"
                        />
                        <small v-if="form.errors.bank" class="text-red-600">{{ form.errors.bank }}</small>
                    </div>

                    <div class="col-span-12">
                        <label class="block text-sm font-medium text-surface-700 mb-2">
                            {{ $t('Global account') }}
                        </label>
                        <InputText
                            v-model="form.global_account"
                            class="w-full"
                        />
                        <small v-if="form.errors.global_account" class="text-red-600">{{ form.errors.global_account }}</small>
                    </div>

                    <div class="col-span-6">
                        <label class="block text-sm font-medium text-surface-700 mb-2">
                            {{ $t('Site pix') }}
                        </label>
                        <InputText
                            v-model="form.pix"
                            class="w-full"
                        />
                        <small v-if="form.errors.pix" class="text-red-600">{{ form.errors.pix }}</small>
                    </div>

                    <div class="col-span-6">
                        <label class="block text-sm font-medium text-surface-700 mb-2">
                            {{ $t('Site paypal') }}
                        </label>
                        <InputText
                            v-model="form.paypal"
                            class="w-full"
                        />
                        <small v-if="form.errors.paypal" class="text-red-600">{{ form.errors.paypal }}</small>
                    </div>

                    <div class="col-span-4">
                        <label class="block text-sm font-medium text-surface-700 mb-2">
                            {{ $t('Site phone') }}
                        </label>
                        <InputText
                            v-model="form.phone"
                            class="w-full"
                        />
                        <small v-if="form.errors.phone" class="text-red-600">{{ form.errors.phone }}</small>
                    </div>

                    <div class="col-span-4">
                        <label class="block text-sm font-medium text-surface-700 mb-2">
                            {{ $t('Site instagram') }}
                        </label>
                        <InputText
                            v-model="form.instagram"
                            class="w-full"
                        />
                        <small v-if="form.errors.instagram" class="text-red-600">{{ form.errors.instagram }}</small>
                    </div>

                    <div class="col-span-4">
                        <label class="block text-sm font-medium text-surface-700 mb-2">
                            {{ $t('Site facebook') }}
                        </label>
                        <InputText
                            v-model="form.facebook"
                            class="w-full"
                        />
                        <small v-if="form.errors.facebook" class="text-red-600">{{ form.errors.facebook }}</small>
                    </div>

                    <!-- Submit Button -->
                    <div class="col-span-12 flex justify-end gap-2 pt-4">
                        <Button
                            :label="$t('Cancel')"
                            severity="secondary"
                            @click="() => {
                                showLoading($t('Canceling...'));
                                try {
                                    const routeName = proxy.route ? proxy.route('sites.index') : '/admin/sites';
                                    proxy.$inertia.visit(routeName, {
                                        onFinish: () => hideLoading(),
                                    });
                                } catch (e) {
                                    hideLoading();
                                    window.location.href = '/admin/sites';
                                }
                            }"
                        />
                        <Button
                            :label="$t('Save')"
                            type="submit"
                            :loading="form.processing"
                            :disabled="form.processing"
                        />
                    </div>
                </form>
            </template>
        </Card>
    </div>
</template>
